'use client';

import { useState, useEffect } from 'react';
import { useWbsStore } from '@/stores/wbsStore';
import { useAuthStore } from '@/stores/authStore';
import { useToast } from '@/components/common/Toast';
import { tasksApi, categoriesApi } from '@/lib/api';

type WbsViewType = 'table' | 'kanban';

interface TaskFiltersProps {
  onAddTask: () => void;
  viewType?: WbsViewType;
  onViewChange?: (view: WbsViewType) => void;
}

export default function TaskFilters({ onAddTask, viewType = 'table', onViewChange }: TaskFiltersProps) {
  const { filters, setFilter, managers, selectedTasks, bulkDeleteTasks } = useWbsStore();
  const { isViewer } = useAuthStore();
  const { success: showSuccess, error: showError } = useToast();
  const [exporting, setExporting] = useState(false);
  const [statusOptions, setStatusOptions] = useState<string[]>(['시작전', '진행중', '컨펌중', '완료']);

  // 카테고리에서 상태 옵션 동적 로드
  useEffect(() => {
    const loadStatusOptions = async () => {
      try {
        const items = await categoriesApi.getItems('task_status');
        if (items.length > 0) {
          setStatusOptions(items.map(item => item.code));
        }
      } catch (error) {
        console.error('Failed to load status options:', error);
      }
    };
    loadStatusOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBulkDelete = async () => {
    if (selectedTasks.size === 0) return;

    if (confirm(`선택한 ${selectedTasks.size}개 작업을 삭제하시겠습니까?`)) {
      await bulkDeleteTasks(Array.from(selectedTasks));
    }
  };

  const handleExportToSheets = async () => {
    try {
      setExporting(true);
      const result = await tasksApi.exportToSheets();
      if (result.success) {
        showSuccess('구글 스프레드시트로 내보내기가 완료되었습니다.');
        // 새 창에서 스프레드시트 열기
        window.open(result.spreadsheet_url, '_blank');
      }
    } catch (error: unknown) {
      const axiosError = error as { response?: { status?: number; data?: { detail?: string } } };
      const errorMessage = axiosError.response?.data?.detail || '구글 스프레드시트 내보내기에 실패했습니다.';
      showError(errorMessage);

      // OAuth 토큰 만료 에러인 경우 시스템 설정 페이지로 이동 안내
      if (axiosError.response?.status === 403 || errorMessage.includes('만료') || errorMessage.includes('OAuth')) {
        if (confirm('Google OAuth 토큰이 만료되었습니다. 시스템 설정 페이지로 이동하시겠습니까?')) {
          window.location.href = '/settings/system';
        }
      }
    } finally {
      setExporting(false);
    }
  };

  return (
    <section className="flex items-center justify-between mb-4">
      <div className="flex space-x-2">
        {!isViewer() && (
          <>
            <button
              onClick={onAddTask}
              className="px-4 py-2 bg-[#1e40af] text-white rounded-lg hover:bg-blue-700 transition flex items-center space-x-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span>작업 추가</span>
            </button>

            <button
              onClick={handleBulkDelete}
              disabled={selectedTasks.size === 0}
              className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              <span>선택 삭제</span>
            </button>
            <button
              onClick={handleExportToSheets}
              disabled={exporting}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {exporting ? (
                <>
                  <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>내보내는 중...</span>
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span>구글 스프레드시트로 내보내기</span>
                </>
              )}
            </button>
          </>
        )}
      </div>

      <div className="flex space-x-2">
        <select
          value={filters.status}
          onChange={(e) => setFilter('status', e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1e40af] focus:border-transparent"
        >
          <option value="">전체 상태</option>
          {statusOptions.map((status) => (
            <option key={status} value={status}>
              {status}
            </option>
          ))}
        </select>

        <select
          value={filters.manager}
          onChange={(e) => setFilter('manager', e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1e40af] focus:border-transparent"
        >
          <option value="">전체 담당자</option>
          {managers.map((manager) => (
            <option key={manager} value={manager}>
              {manager}
            </option>
          ))}
        </select>

        {/* 뷰 토글 */}
        {onViewChange && (
          <div className="flex border border-gray-300 rounded-lg overflow-hidden ml-2">
            <button
              onClick={() => onViewChange('table')}
              className={`px-3 py-2 text-sm flex items-center gap-1.5 transition ${
                viewType === 'table' ? 'bg-[#1e40af] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
              title="테이블 뷰"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M3 14h18M3 6h18M3 18h18" />
              </svg>
              <span>테이블</span>
            </button>
            <button
              onClick={() => onViewChange('kanban')}
              className={`px-3 py-2 text-sm flex items-center gap-1.5 transition ${
                viewType === 'kanban' ? 'bg-[#1e40af] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
              title="칸반 뷰"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
              </svg>
              <span>칸반</span>
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
